const DEFAULT_CONTEXT_LINES = 3;
const DEFAULT_MAX_CHANGED_LINES = 500;
const MAX_LINE_CHARACTERS = 2000;

type WorkspaceMemoryDiffCellKind =
	| 'added'
	| 'context'
	| 'empty'
	| 'omitted'
	| 'removed';

interface WorkspaceMemoryDiffCell {
	content: string;
	kind: WorkspaceMemoryDiffCellKind;
	lineNumber?: number;
}

interface WorkspaceMemorySideBySideDiffRow {
	after: WorkspaceMemoryDiffCell;
	before: WorkspaceMemoryDiffCell;
	kind: 'changed' | 'context' | 'omitted';
}

function splitLines(text: string) {
	return text.split('\n');
}

function truncateLine(line: string) {
	if (line.length <= MAX_LINE_CHARACTERS) {
		return line;
	}

	return `${line.slice(0, MAX_LINE_CHARACTERS)}... [truncated]`;
}

function createCell(
	kind: WorkspaceMemoryDiffCellKind,
	content: string,
	lineNumber?: number
): WorkspaceMemoryDiffCell {
	return {
		content: truncateLine(content),
		kind,
		lineNumber,
	};
}

function createContextRow(
	content: string,
	beforeLineNumber: number,
	afterLineNumber: number
): WorkspaceMemorySideBySideDiffRow {
	return {
		after: createCell('context', content, afterLineNumber),
		before: createCell('context', content, beforeLineNumber),
		kind: 'context',
	};
}

function createOmittedRow(content: string): WorkspaceMemorySideBySideDiffRow {
	return {
		after: createCell('omitted', content),
		before: createCell('omitted', content),
		kind: 'omitted',
	};
}

export function buildWorkspaceMemorySideBySideDiffRows(
	originalMemory: string,
	condensedMemory: string,
	maxChangedLines = DEFAULT_MAX_CHANGED_LINES,
	contextLines = DEFAULT_CONTEXT_LINES
) {
	const originalLines = splitLines(originalMemory);
	const condensedLines = splitLines(condensedMemory);
	const rows: WorkspaceMemorySideBySideDiffRow[] = [];

	let prefixLength = 0;
	while (
		prefixLength < originalLines.length &&
		prefixLength < condensedLines.length &&
		originalLines[prefixLength] === condensedLines[prefixLength]
	) {
		prefixLength += 1;
	}

	let suffixLength = 0;
	while (
		suffixLength + prefixLength < originalLines.length &&
		suffixLength + prefixLength < condensedLines.length &&
		originalLines[originalLines.length - 1 - suffixLength] ===
			condensedLines[condensedLines.length - 1 - suffixLength]
	) {
		suffixLength += 1;
	}

	const originalChangeStart = prefixLength;
	const originalChangeEnd = originalLines.length - suffixLength;
	const condensedChangeStart = prefixLength;
	const condensedChangeEnd = condensedLines.length - suffixLength;
	const contextStart = Math.max(0, prefixLength - contextLines);
	const contextEnd = Math.min(
		originalLines.length,
		originalChangeEnd + contextLines
	);

	if (contextStart > 0) {
		rows.push(createOmittedRow(`${contextStart} unchanged lines omitted`));
	}

	for (let index = contextStart; index < prefixLength; index += 1) {
		rows.push(createContextRow(originalLines[index], index + 1, index + 1));
	}

	const removedLines = originalLines.slice(
		originalChangeStart,
		originalChangeEnd
	);
	const addedLines = condensedLines.slice(
		condensedChangeStart,
		condensedChangeEnd
	);
	const removedBudget = Math.min(
		removedLines.length,
		Math.ceil(maxChangedLines / 2)
	);
	const addedBudget = Math.min(
		addedLines.length,
		maxChangedLines - removedBudget
	);
	const changedRowCount = Math.max(removedBudget, addedBudget);

	for (let index = 0; index < changedRowCount; index += 1) {
		const before =
			index < removedBudget
				? createCell(
						'removed',
						removedLines[index],
						originalChangeStart + index + 1
					)
				: createCell('empty', '');
		const after =
			index < addedBudget
				? createCell(
						'added',
						addedLines[index],
						condensedChangeStart + index + 1
					)
				: createCell('empty', '');

		rows.push({
			after,
			before,
			kind: 'changed',
		});
	}

	if (removedLines.length > removedBudget) {
		rows.push(
			createOmittedRow(
				`${removedLines.length - removedBudget} removed lines omitted`
			)
		);
	}

	if (addedLines.length > addedBudget) {
		rows.push(
			createOmittedRow(`${addedLines.length - addedBudget} added lines omitted`)
		);
	}

	for (let index = originalChangeEnd; index < contextEnd; index += 1) {
		const afterLineNumber = index - originalChangeEnd + condensedChangeEnd + 1;
		rows.push(
			createContextRow(originalLines[index], index + 1, afterLineNumber)
		);
	}

	if (contextEnd < originalLines.length) {
		rows.push(
			createOmittedRow(
				`${originalLines.length - contextEnd} unchanged lines omitted`
			)
		);
	}

	return rows;
}
